import type { ShortcutDefinition } from '@/lib/keyboard/keyboard-shortcuts'

/**
 * Keyboard shortcuts active when the editor is in text-editing mode (shape text or text boxes).
 * This is the authoritative registry for text-editor key bindings — add new ones here first,
 * then implement the handler in the relevant component.
 *
 * Keys that also appear in keyboard-shortcuts.ts (copy, cut, paste, selectAll, undo, redo) must
 * stay in sync with that file; the text-editor mode handlers take precedence over canvas handlers
 * because the textarea's onKeyDown calls stopPropagation().
 */
export const textEditorShortcuts = {
  copy: { mac: [['mod', 'C']], windows: [['mod', 'C']] },
  cut: { mac: [['mod', 'X']], windows: [['mod', 'X']] },
  // Registered for documentation/parity with keyboard-shortcuts.ts, but deliberately NOT wired to
  // a keydown handler (WMPCM-9199). Intercepting mod+V and preventDefaulting it suppressed the
  // browser's own `paste` event, which is what `ShapeTextEditor`'s `onPaste` needs in order to read
  // `event.clipboardData` and keep the source formatting. Do not add a handler for this key.
  paste: { mac: [['mod', 'V']], windows: [['mod', 'V']] },
  selectAll: { mac: [['mod', 'A']], windows: [['mod', 'A']] },
  undo: { mac: [['mod', 'Z']], windows: [['mod', 'Z']] },
  redo: { mac: [['mod', 'Y']], windows: [['mod', 'Y']] },
  escape: { mac: [['Escape']], windows: [['Escape']] },
  // Delete/Backspace are deliberately NOT registered here. Every entry above pairs with an
  // app-level handler that preventDefaults; Delete/Backspace in text-editing mode are handled
  // natively by the contentEditable and the textarea, so there is nothing to register. More
  // importantly, `matchesTextEditorShortcut` below enforces exact modifier equality, so an entry
  // would match only the bare keys and silently exclude Ctrl+Backspace (delete-word) and
  // Alt+Backspace. The canvas-side protection that stops these keys reaching the object delete is
  // the `isTextEditSessionActive()` guard in `center-canvas/handlers/canvas-key-action-handlers.ts`
  // (WMPCM-7044), and it must stay modifier-agnostic — so it must not be gated on this registry.
} as const satisfies Record<string, ShortcutDefinition>
